import { Fragment } from 'react';

import { StatusDot } from '@playbooks/molecules/dots/status-dot';
import { Skeleton } from '@playbooks/molecules/skeletons';
import type { CardProps as PrimitiveCardProps } from '@playbooks/ui/components/card';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@playbooks/ui/components/card';
import { CardMedia } from '@playbooks/ui/components/card';
import { Icon, resolveIcon } from '@playbooks/ui/components/icon';
import { Link } from '@playbooks/ui/components/link';
import { cn } from '@playbooks/ui/lib/utils';

import type { CardContentProps, CardIndicatorProps, CardNavigationProps } from './types';

type PillCardProps = Pick<
	CardContentProps,
	| 'actions'
	| 'badges'
	| 'className'
	| 'icon'
	| 'loading'
	| 'media'
	| 'photo'
	| 'pretitle'
	| 'subtitle'
	| 'text'
	| 'title'
> &
	CardIndicatorProps &
	CardNavigationProps & {
		overlayActions?: React.ReactNode;
		render?: PrimitiveCardProps['render'];
	};
type PillLinkCardProps = Omit<PillCardProps, 'render'> & Required<Pick<CardNavigationProps, 'href'>>;

export const PillCard = ({
	icon,
	overlayActions,
	badges,
	indicatorStatus,
	media,
	photo,
	pretitle,
	title,
	subtitle,
	text,
	href,
	target,
	loading,
	className = '',
	actions,
	linkProps,
	render,
}: PillCardProps) => {
	const rootInteractive = Boolean(render);
	const renderedHeader = (
		<span className='inline-block shrink-0 relative'>
			{media ? (
				<Fragment>{media}</Fragment>
			) : (
				<CardHeader className='aspect-[1/1] rounded-control flex-middle shrink-0 overflow-hidden w-9'>
					{photo ? (
						<img src={photo} alt={title + ' photo'} className='w-full' />
					) : (
						<CardMedia>
							<Icon {...resolveIcon(icon, 'fad')} aria-hidden />
						</CardMedia>
					)}
				</CardHeader>
			)}
			<StatusDot status={indicatorStatus} className='absolute -top-1 -right-1 z-2' />
		</span>
	);

	// Theme
	const rootClassName = cn(
		'md:flex-between gap-3 p-3 hover:border-border-strong hover:bg-surface-card-hover',
		className,
	);

	// Render
	if (loading) return <Skeleton layout='pill' className={className} />;

	return (
		<Card size='sm' render={render} className={rootClassName}>
			{overlayActions}
			<span className='flex-start-top grow gap-3 w-full'>
				{renderedHeader}
				<CardContent className='text-left grow'>
					{pretitle && <div>{pretitle}</div>}
					{title && (
						<span className='flex-start gap-4'>
							<CardTitle>
								{href && !rootInteractive ? (
									<Link
										variant='ghost'
										href={href}
										target={target}
										{...linkProps}
										className='hover:underline h-auto p-0'>
										{title}
									</Link>
								) : (
									title
								)}
							</CardTitle>
							{badges && <span className='flex-start gap-2'>{badges}</span>}
						</span>
					)}
					{subtitle && <CardDescription className='line-clamp-2'>{subtitle}</CardDescription>}
					{text && <CardDescription className='line-clamp-2'>{text}</CardDescription>}
				</CardContent>
			</span>
			{actions && <CardAction className='hidden md:flex-end shrink-0 self-center'>{actions}</CardAction>}
		</Card>
	);
};

export const PillLinkCard = ({
	className = '',
	href,
	target,
	rel,
	linkProps,
	actions,
	overlayActions,
	...props
}: PillLinkCardProps) => {
	// Theme
	const rootClassName = cn('group w-full h-auto p-0', className);
	const hasNestedInteraction = Boolean(actions || overlayActions);

	// Render
	return (
		<PillCard
			{...props}
			actions={actions}
			overlayActions={overlayActions}
			href={href}
			target={target}
			rel={rel}
			linkProps={linkProps}
			render={
				hasNestedInteraction ? undefined : (
					<Link
						variant='ghost'
						aria-label={String(props.title)}
						href={href}
						target={target}
						rel={rel}
						{...linkProps}
						className={rootClassName}
					/>
				)
			}
		/>
	);
};

export type { PillCardProps, PillLinkCardProps };
